import { useCallback, useEffect, useState } from "react"
import api from "../services/api"
import AdminLayout from "../components/AdminLayout"
import FileIcon from "../components/FileIcon"
import { useAdminFeedback } from "../context/AdminFeedbackContext"

const C = {
    navy: "#002B54",
    navyLight: "#163354",
    navyMid: "#1e4070",
    blue: "#1a6fc4",
    blueLight: "#3b8de0",
    bluePale: "#e8f0fb",
    bluePale2: "#dceaf8",
    white: "#ffffff",
    offWhite: "#f5f7fa",
    gray100: "#eef1f5",
    gray200: "#dde2ea",
    gray300: "#c3cad5",
    gray400: "#94a0b0",
    gray500: "#64748b",
    gray700: "#334155",
    green: "#15803d",
    greenBg: "#dcfce7",
    greenBorder: "#86efac",
    red: "#dc2626",
    redBg: "#fef2f2",
    redBorder: "#fca5a5",
    orange: "#F4811F",
    orangeBg: "#fff4eb",
}

function ApproveBtn({ onClick }) {
    const [hovered, setHovered] = useState(false)
    return (
        <button
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "8px 18px",
                fontSize: "11px", fontWeight: "800",
                letterSpacing: "0.6px", textTransform: "uppercase",
                color: hovered ? C.white : C.navy,
                background: hovered
                    ? `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`
                    : C.white,
                border: `1.5px solid ${hovered ? C.navy : C.gray200}`,
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.18s ease",
                boxShadow: hovered ? `0 4px 14px rgba(12,35,64,0.2)` : "none",
                whiteSpace: "nowrap",
            }}
        >
            Approve
        </button>
    )
}

function DeleteBtn({ onClick }) {
    const [hovered, setHovered] = useState(false)
    return (
        <button
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "8px 14px",
                fontSize: "11px", fontWeight: "800",
                letterSpacing: "0.6px", textTransform: "uppercase",
                color: hovered ? "#ef4444" : C.gray500,
                background: hovered ? "#fef2f2" : C.white,
                border: `1.5px solid ${hovered ? "#fca5a5" : C.gray200}`,
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.18s ease",
                whiteSpace: "nowrap",
            }}
            title="Delete Request"
        >
            Delete
        </button>
    )
}

function RevokeBtn({ onClick }) {
    const [hovered, setHovered] = useState(false)
    return (
        <button
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "8px 18px",
                fontSize: "11px", fontWeight: "800",
                letterSpacing: "0.6px", textTransform: "uppercase",
                color: hovered ? C.white : "#dc2626",
                background: hovered
                    ? "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)"
                    : C.white,
                border: `1.5px solid ${hovered ? "#dc2626" : "#fca5a5"}`,
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.18s ease",
                boxShadow: hovered ? "0 4px 14px rgba(220,38,38,0.25)" : "none",
                whiteSpace: "nowrap",
            }}
        >
            Revoke
        </button>
    )
}

function RejectBtn({ onClick }) {
    const [hovered, setHovered] = useState(false)
    return (
        <button
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "8px 18px",
                fontSize: "11px", fontWeight: "800",
                letterSpacing: "0.6px", textTransform: "uppercase",
                color: hovered ? C.white : C.gray500,
                background: hovered
                    ? "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)"
                    : C.white,
                border: `1.5px solid ${hovered ? "#6b7280" : C.gray200}`,
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.18s ease",
                whiteSpace: "nowrap",
            }}
        >
            Reject
        </button>
    )
}

function StatusPill({ status }) {
    const colorMap = {
        pending: { bg: C.bluePale, border: C.bluePale2, text: C.blue, label: "Pending" },
        approved: { bg: C.greenBg, border: C.greenBorder, text: C.green, label: "Approved" },
        revoked: { bg: C.redBg, border: C.redBorder, text: C.red, label: "Revoked" },
        rejected: { bg: C.gray100, border: C.gray200, text: C.gray500, label: "Rejected" },
    }
    const c = colorMap[status] || colorMap.pending
    return (
        <span style={{
            display: "inline-flex", alignItems: "center",
            padding: "4px 10px", borderRadius: "20px",
            fontSize: "10px", fontWeight: "800", textTransform: "uppercase",
            letterSpacing: "0.5px",
            background: c.bg, color: c.text, border: `1px solid ${c.border}`,
        }}>
            {c.label}
        </span>
    )
}

function Avatar({ name, email }) {
    const seed = name || email || "Guest"
    const initials = name
        ? name.split(/\s+/).filter(Boolean).map((part) => part[0]).join("").slice(0, 2).toUpperCase()
        : seed.slice(0, 2).toUpperCase()
    const colors = [
        { bg: "#dceaf8", text: "#1a6fc4" },
        { bg: "#e0f2fe", text: "#0369a1" },
        { bg: "#ede9fe", text: "#6d28d9" },
        { bg: "#fce7f3", text: "#be185d" },
        { bg: "#dcfce7", text: "#15803d" },
    ]
    const col = colors[seed.charCodeAt(0) % colors.length]
    return (
        <div style={{
            width: "36px", height: "34px", borderRadius: "10px",
            background: col.bg, color: col.text,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "12px", fontWeight: "800", flexShrink: 0,
            letterSpacing: "0.5px",
        }}>
            {initials}
        </div>
    )
}

function Row({ r, onApprove, onDelete, onRevoke, onReject, isLast }) {
    const [hovered, setHovered] = useState(false)
    const type = r.status
    const dateStr = r.requestedAt
        ? new Date(r.requestedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
        : null

    return (
        <tr
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                background: hovered ? C.offWhite : C.white,
                transition: "background 0.15s",
                borderBottom: isLast ? "none" : `1px solid ${C.gray100}`,
            }}
        >
            <td style={{ padding: "16px 20px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <Avatar name={r.requesterName} email={r.email} />
                    <div>
                        <div style={{ fontSize: "13px", fontWeight: "700", color: C.navy, lineHeight: 1.3 }}>
                            {r.requesterName || r.email}
                        </div>
                        {dateStr && (
                            <div style={{ fontSize: "10px", color: C.gray400, marginTop: "3px", fontWeight: "600", letterSpacing: "0.3px" }}>
                                {dateStr}
                            </div>
                        )}
                    </div>
                </div>
            </td>

            <td style={{ padding: "16px 20px" }}>
                {r.company ? (
                    <div style={{ fontSize: "12px", fontWeight: "600", color: C.gray700 }}>
                        {r.company}
                    </div>
                ) : (
                    <span style={{ fontSize: "12px", color: C.gray300, fontStyle: "italic" }}>—</span>
                )}
            </td>

            <td style={{ padding: "16px 20px" }}>
                <div style={{
                    display: "inline-flex", alignItems: "center", gap: "5px",
                    padding: "4px 10px", borderRadius: "20px",
                    fontSize: "10px", fontWeight: "800", textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    background: r.userRole === "customer" ? C.bluePale : C.gray100,
                    color: r.userRole === "customer" ? C.blue : C.gray500,
                    border: `1px solid ${r.userRole === "customer" ? C.bluePale2 : C.gray200}`
                }}>
                    {r.userRole === "customer" ? "Client" : "Prospect"}
                </div>
            </td>

            <td style={{ padding: "16px 20px" }}>
                {(r.documentName || r.documentId?.title) ? (
                    <div style={{
                        display: "inline-flex", alignItems: "center", gap: "6px",
                        background: C.bluePale, border: `1px solid ${C.bluePale2}`,
                        borderRadius: "7px", padding: "5px 10px",
                    }}>
                        <FileIcon size={14} color={C.blue} />
                        <span style={{ fontSize: "12px", fontWeight: "700", color: C.blue }}>
                            {r.documentName || r.documentId?.title}
                        </span>
                    </div>
                ) : (
                    <span style={{ fontSize: "12px", color: C.gray400, fontStyle: "italic" }}>—</span>
                )}
            </td>

            <td style={{ padding: "16px 20px", maxWidth: "220px" }}>
                {type === "rejected" && r.rejectionReason ? (
                    <div style={{
                        fontSize: "12px", color: C.gray500, fontStyle: "italic",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }} title={r.rejectionReason}>
                        "{r.rejectionReason}"
                    </div>
                ) : r.reason ? (
                    <div style={{
                        fontSize: "12px", color: C.gray500, fontStyle: "italic",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }} title={r.reason}>
                        "{r.reason}"
                    </div>
                ) : (
                    <span style={{ fontSize: "12px", color: C.gray300, fontStyle: "italic" }}>No reason provided</span>
                )}
            </td>

            <td style={{ padding: "16px 20px" }}>
                <StatusPill status={type} />
            </td>

            <td style={{ padding: "16px 20px", textAlign: "right" }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: "10px" }}>
                    <DeleteBtn onClick={() => onDelete(r._id)} />
                    {type === "pending" && <RejectBtn onClick={() => onReject(r._id)} />}
                    {type === "pending" && <ApproveBtn onClick={() => onApprove(r._id)} />}
                    {type === "approved" && <RevokeBtn onClick={() => onRevoke(r._id)} />}
                    {type === "revoked" && <ApproveBtn onClick={() => onApprove(r._id)} />}
                    {type === "rejected" && <ApproveBtn onClick={() => onApprove(r._id)} />}
                </div>
            </td>
        </tr>
    )
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export default function AdminDashboard() {
    const { toast, confirm } = useAdminFeedback()
    const [records, setRecords] = useState([])
    const [loading, setLoading] = useState(true)
    const [statusFilter, setStatusFilter] = useState("all")
    const [requesterFilter, setRequesterFilter] = useState("")
    const [companyFilter, setCompanyFilter] = useState("")
    const [documentFilter, setDocumentFilter] = useState("")
    const [appliedFilters, setAppliedFilters] = useState({
        status: "all",
        requester: "",
        company: "",
        document: "",
    })
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const [total, setTotal] = useState(0)
    const [totalPages, setTotalPages] = useState(1)
    const [counts, setCounts] = useState({ pending: 0, approved: 0, revoked: 0, rejected: 0 })

    const loadData = useCallback(async () => {
        try {
            setLoading(true)
            const params = new URLSearchParams({ page, limit, status: appliedFilters.status || "all" })
            if (appliedFilters.requester?.trim()) params.set("requester", appliedFilters.requester.trim())
            if (appliedFilters.company?.trim()) params.set("company", appliedFilters.company.trim())
            if (appliedFilters.document?.trim()) params.set("document", appliedFilters.document.trim())
            const res = await api.get(`/admin/access-requests?${params}`)
            setRecords(res.data.records)
            setTotal(res.data.total)
            setTotalPages(res.data.totalPages)
            setCounts(res.data.counts)
        } catch (err) {
            console.error("Error loading admin data:", err)
        } finally {
            setLoading(false)
        }
    }, [page, limit, appliedFilters])

    useEffect(() => { loadData() }, [loadData])

    const handleSearch = () => {
        setAppliedFilters({
            status: statusFilter,
            requester: requesterFilter,
            company: companyFilter,
            document: documentFilter,
        })
        setPage(1)
    }

    const clearSearch = () => {
        setStatusFilter("all")
        setRequesterFilter("")
        setCompanyFilter("")
        setDocumentFilter("")
        setAppliedFilters({ status: "all", requester: "", company: "", document: "" })
        setPage(1)
    }

    const handleSingleApprove = async (requestId) => {
        const request = records.find(r => String(r._id) === String(requestId))
        if (!request) return

        const ok = await confirm(`Approve access for ${request.requesterName || request.email}?`)
        if (!ok) return

        try {
            const adminStr = localStorage.getItem("user")
            const admin = adminStr ? JSON.parse(adminStr) : null
            const requestedDocId = request.documentId?._id || request.documentId
            const documentIds = requestedDocId ? [requestedDocId] : []

            await api.post(`/admin/approve/${requestId}`, {
                adminId: admin?._id,
                adminEmail: admin?.email,
                documentIds
            })

            toast.success("Access approved")
            loadData()
        } catch (err) {
            console.error(err)
            toast.error("Approval failed")
        }
    }

    const deleteRequest = async (id) => {
        const ok = await confirm({ message: "Delete this request forever? This cannot be undone.", danger: true, confirmLabel: "Delete" })
        if (!ok) return
        try {
            await api.delete(`/admin/access-requests/${id}`)
            toast.success("Request deleted")
            loadData()
        } catch {
            toast.error("Delete failed")
        }
    }

    const revokeAccess = async (requestId) => {
        const request = records.find(r => String(r._id) === String(requestId))
        if (!request) return

        const docName = request.documentName || request.documentId?.title || "this document"
        const ok = await confirm({
            message: `Revoke ${request.requesterName || request.email}'s access to "${docName}"?`,
            danger: true,
            confirmLabel: "Revoke",
        })
        if (!ok) return

        try {
            await api.post(`/admin/revoke/${requestId}`)
            toast.success("Access revoked successfully")
            loadData()
        } catch (err) {
            console.error(err)
            toast.error("Failed to revoke access")
        }
    }

    const rejectRequest = async (requestId) => {
        const request = records.find(r => String(r._id) === String(requestId))
        if (!request) return

        const reason = await confirm({
            title: "Reject request",
            message: `Reject ${request.requesterName || request.email}'s request for "${request.documentName || "this document"}"?`,
            inputLabel: "Reason (optional, shown to the requester)",
            confirmLabel: "Reject",
            danger: true,
        })
        if (reason === null) return // user cancelled

        try {
            await api.post(`/admin/reject/${requestId}`, { reason })
            toast.success("Request rejected")
            loadData()
        } catch (err) {
            console.error(err)
            toast.error("Failed to reject request")
        }
    }

    function getPageNumbers() {
        const pages = []
        const maxVisible = 5
        if (totalPages <= maxVisible + 2) {
            for (let i = 1; i <= totalPages; i++) pages.push(i)
        } else {
            pages.push(1)
            let start = Math.max(2, page - 1)
            let end = Math.min(totalPages - 1, page + 1)
            if (page <= 3) { start = 2; end = Math.min(maxVisible, totalPages - 1) }
            else if (page >= totalPages - 2) { start = Math.max(totalPages - maxVisible + 1, 2); end = totalPages - 1 }
            if (start > 2) pages.push("...")
            for (let i = start; i <= end; i++) pages.push(i)
            if (end < totalPages - 1) pages.push("...")
            pages.push(totalPages)
        }
        return pages
    }

    const totalRequests = counts.pending + counts.approved + counts.revoked + counts.rejected

    return (
        <AdminLayout
            title="Access Requests"
            description="Review and manage access grants for private security documents."
        >
            <div style={{
                width: "100%", maxWidth: "none", margin: 0,
                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                paddingBottom: "48px",
            }}>

                {/* ── Stats Row ─────────────────────────────── */}
                <div style={{
                    display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
                    gap: "12px", marginBottom: "24px",
                }}>
                    {[
                        { num: totalRequests, label: "Total", accent: C.orange },
                        { num: counts.approved, label: "Approved", accent: C.green },
                        { num: counts.pending, label: "Pending", accent: C.blue },
                        { num: counts.rejected + counts.revoked, label: "Rejected/revoked", accent: C.red },
                    ].map(({ num, label, accent }) => (
                        <div key={label} style={{
                            background: C.white,
                            border: `1px solid ${C.gray200}`,
                            borderRadius: "14px",
                            padding: "18px 22px",
                            boxShadow: "0 1px 8px rgba(12,35,64,0.05)",
                        }}>
                            <div style={{ fontSize: "26px", fontWeight: "800", color: accent, lineHeight: 1 }}>{num}</div>
                            <div style={{ fontSize: "11px", fontWeight: "600", color: C.gray400, textTransform: "uppercase", letterSpacing: "0.5px", marginTop: "6px" }}>{label}</div>
                        </div>
                    ))}
                </div>

                {/* ── Table Card ───────────────────────────── */}
                <div style={{
                    background: C.white,
                    border: `1px solid ${C.gray200}`,
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 2px 16px rgba(12,35,64,0.07)",
                }}>

                    {/* Card Header */}
                    <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "18px 24px",
                        borderBottom: `1px solid ${C.gray100}`,
                        background: C.offWhite,
                    }}>
                        <div>
                            <div style={{ fontSize: "14px", fontWeight: "800", color: C.navy }}>
                                Access Requests
                            </div>
                            <div style={{ fontSize: "11px", color: C.gray400 }}>
                                Filter by status, requestor, company, or document
                            </div>
                        </div>
                        <span style={{ fontSize: "12px", fontWeight: "700", color: C.gray500 }}>
                            {total} result{total === 1 ? "" : "s"}
                        </span>
                    </div>

                    {/* Filters */}
                    <div style={{
                        padding: "14px 24px",
                        borderBottom: `1px solid ${C.gray100}`,
                        background: C.white,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                        alignItems: "center",
                    }}>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{
                                padding: "8px 10px",
                                borderRadius: "8px",
                                border: `1px solid ${C.gray200}`,
                                fontSize: "12px",
                                fontWeight: "600",
                                color: C.navy,
                                background: C.offWhite,
                                cursor: "pointer",
                                outline: "none",
                                minWidth: "160px",
                            }}
                        >
                            <option value="all">All statuses</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected_revoked">Rejected/revoked</option>
                        </select>
                        <input
                            type="text"
                            placeholder="Requestor name"
                            value={requesterFilter}
                            onChange={(e) => setRequesterFilter(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            style={{
                                padding: "8px 12px",
                                borderRadius: "8px",
                                border: `1px solid ${C.gray200}`,
                                fontSize: "13px",
                                color: C.navy,
                                outline: "none",
                                flex: "1 1 140px",
                                minWidth: "140px",
                            }}
                        />
                        <input
                            type="text"
                            placeholder="Company"
                            value={companyFilter}
                            onChange={(e) => setCompanyFilter(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            style={{
                                padding: "8px 12px",
                                borderRadius: "8px",
                                border: `1px solid ${C.gray200}`,
                                fontSize: "13px",
                                color: C.navy,
                                outline: "none",
                                flex: "1 1 140px",
                                minWidth: "140px",
                            }}
                        />
                        <input
                            type="text"
                            placeholder="Document name"
                            value={documentFilter}
                            onChange={(e) => setDocumentFilter(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            style={{
                                padding: "8px 12px",
                                borderRadius: "8px",
                                border: `1px solid ${C.gray200}`,
                                fontSize: "13px",
                                color: C.navy,
                                outline: "none",
                                flex: "1 1 140px",
                                minWidth: "140px",
                            }}
                        />
                        <button onClick={handleSearch} style={{
                            padding: "8px 18px", borderRadius: "8px",
                            background: `linear-gradient(135deg, ${C.navy}, ${C.navyMid})`,
                            color: C.white, border: "none", fontSize: "12px", fontWeight: "700", cursor: "pointer",
                            whiteSpace: "nowrap",
                        }}>Search</button>
                        <button onClick={clearSearch} style={{
                            background: "none", border: "none", color: C.gray400,
                            fontSize: "12px", fontWeight: "700", cursor: "pointer", padding: "4px 8px",
                            whiteSpace: "nowrap",
                        }}>Clear</button>
                    </div>

                    {/* Table */}
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ borderBottom: `1.5px solid ${C.gray100}`, background: C.offWhite }}>
                                    {["Requester", "Company", "User Type", "Document", "Reason", "Status", "Action"].map((h, i, arr) => (
                                        <th key={h} style={{
                                            padding: "12px 20px",
                                            fontSize: "10px", fontWeight: "800",
                                            textTransform: "uppercase", letterSpacing: "0.8px",
                                            color: C.gray400,
                                            textAlign: (i === arr.length - 1) ? "right" : "left",
                                            whiteSpace: "nowrap",
                                        }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={7} style={{ padding: "60px 24px", textAlign: "center" }}>
                                            <p style={{ color: C.gray400, fontWeight: 600, fontSize: "14px" }}>Loading...</p>
                                        </td>
                                    </tr>
                                ) : records.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} style={{ padding: "60px 24px", textAlign: "center" }}>
                                            <p style={{ fontSize: "14px", color: C.gray400, fontStyle: "italic" }}>
                                                No matching requests found.
                                            </p>
                                        </td>
                                    </tr>
                                ) : (
                                    records.map((r, idx) => (
                                        <Row
                                            key={r._id}
                                            r={r}
                                            onApprove={handleSingleApprove}
                                            onDelete={deleteRequest}
                                            onRevoke={revokeAccess}
                                            onReject={rejectRequest}
                                            isLast={idx === records.length - 1}
                                        />
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    {records.length > 0 && (
                        <div style={{
                            padding: "14px 24px", borderTop: `1px solid ${C.gray100}`, background: C.offWhite,
                            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px",
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <span style={{ fontSize: "12px", color: C.gray500, fontWeight: "600" }}>Rows per page:</span>
                                    <select
                                        value={limit}
                                        onChange={(e) => { setLimit(Number(e.target.value)); setPage(1) }}
                                        style={{
                                            padding: "4px 8px", borderRadius: "6px",
                                            border: `1px solid ${C.gray200}`, fontSize: "12px", fontWeight: "700",
                                            color: C.navy, background: C.white, cursor: "pointer", outline: "none",
                                        }}
                                    >
                                        {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                                <span style={{ fontSize: "12px", color: C.gray400 }}>
                                    {Math.min((page - 1) * limit + 1, total)}–{Math.min(page * limit, total)} of {total}
                                </span>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <button onClick={() => setPage(1)} disabled={page <= 1} title="First page"
                                    style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.gray200}`, background: C.white, fontSize: "13px", color: page <= 1 ? C.gray300 : C.navy, cursor: page <= 1 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>«</button>
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} title="Previous page"
                                    style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.gray200}`, background: C.white, fontSize: "13px", color: page <= 1 ? C.gray300 : C.navy, cursor: page <= 1 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>

                                {getPageNumbers().map((p, i) =>
                                    p === "..." ? (
                                        <span key={`ellipsis-${i}`} style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", color: C.gray400, fontWeight: "700" }}>…</span>
                                    ) : (
                                        <button key={p} onClick={() => setPage(p)}
                                            style={{ width: "32px", height: "32px", borderRadius: "8px", border: p === page ? "none" : `1px solid ${C.gray200}`, background: p === page ? `linear-gradient(135deg, ${C.navy}, ${C.navyMid})` : C.white, fontSize: "12px", fontWeight: "700", color: p === page ? C.white : C.navy, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>{p}</button>
                                    )
                                )}

                                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} title="Next page"
                                    style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.gray200}`, background: C.white, fontSize: "13px", color: page >= totalPages ? C.gray300 : C.navy, cursor: page >= totalPages ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
                                <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} title="Last page"
                                    style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.gray200}`, background: C.white, fontSize: "13px", color: page >= totalPages ? C.gray300 : C.navy, cursor: page >= totalPages ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>»</button>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </AdminLayout>
    )
}